import type { LucideIcon } from 'lucide-react';
import {
    ActivityIcon,
    BanIcon,
    BoxIcon,
    ChartNoAxesCombinedIcon,
    ChevronRightSquareIcon,
    ClipboardCheckIcon,
    ClockIcon,
    CalendarClockIcon,
    ContactRoundIcon,
    DoorOpenIcon,
    FileEditIcon,
    FileSearchIcon,
    GavelIcon,
    HeadphonesIcon,
    IdCardIcon,
    ListIcon,
    SettingsIcon,
    ShieldCheckIcon,
    ShieldIcon,
    ShieldQuestionIcon,
    SlidersHorizontalIcon,
    UserSquare2Icon,
    UsersIcon,
    ZapIcon,
} from 'lucide-react';
import { useLocation, useRoute } from 'wouter';
import MainPageLink from '@/components/MainPageLink';
import { useAdminPerms } from '@/hooks/auth';
import { useFcAdminAccess } from '@/hooks/fcAdmin';
import { cn } from '@/lib/utils';
import type { FcAdminOperation } from '@shared/fcAdminApiTypes';
import ServerControls from './ServerSidebar/ServerControls';
import ServerSchedule from './ServerSidebar/ServerSchedule';

type NavigationItem = {
    href: string;
    label: string;
    icon: LucideIcon;
    permission?: string;
    capability?: FcAdminOperation | FcAdminOperation[];
    master?: boolean;
    advanced?: boolean;
};

type NavigationSection = {
    label: string;
    items: NavigationItem[];
};

const sections: NavigationSection[] = [
    {
        label: 'Übersicht',
        items: [
            { href: '/', label: 'Dashboard', icon: ActivityIcon },
        ],
    },
    {
        label: 'Serverbetrieb',
        items: [
            { href: '/server/console', label: 'Live-Konsole', icon: ChevronRightSquareIcon, permission: 'console.view' },
            { href: '/server/resources', label: 'Resources', icon: BoxIcon },
            { href: '/server/performance', label: 'Performance', icon: ActivityIcon },
            { href: '/server/scheduler', label: 'Scheduler', icon: CalendarClockIcon },
            { href: '/insights/player-drops', label: 'Player Drops', icon: DoorOpenIcon },
            { href: '/server/server-log', label: 'Server Log', icon: ListIcon, permission: 'server.log.view' },
            { href: '/server/cfg-editor', label: 'CFG Editor', icon: FileEditIcon, permission: 'server.cfg.editor' },
        ],
    },
    {
        label: 'Spieler & Moderation',
        items: [
            { href: '/fc/players', label: 'Spieler', icon: UsersIcon, capability: ['players.directory', 'players.list'] },
            { href: '/fc/moderation', label: 'Bans & Warns', icon: GavelIcon, capability: 'moderation.list' },
            { href: '/fc/jail', label: 'Admin Jail', icon: BanIcon, capability: 'jail.list' },
            { href: '/fc/support', label: 'Support', icon: HeadphonesIcon, capability: 'support.list' },
            { href: '/fc/entry', label: 'Einreise', icon: ShieldQuestionIcon, capability: 'entry.list' },
        ],
    },
    {
        label: 'Verwaltung',
        items: [
            { href: '/fc/player-ids', label: 'Spieler-IDs', icon: IdCardIcon, capability: 'playerids.directory' },
            { href: '/fc/playtime', label: 'Spielzeit', icon: ClockIcon, capability: 'playtime.read' },
            { href: '/fc/team', label: 'Team & Rollen', icon: ShieldIcon, capability: 'team.list' },
            { href: '/fc/permissions', label: 'Rechtematrix', icon: ContactRoundIcon, capability: 'permissions.read' },
        ],
    },
    {
        label: 'Welt',
        items: [
            { href: '/fc/actions', label: 'World Settings', icon: SlidersHorizontalIcon, capability: ['world.announcement', 'world.weather', 'world.time'] },
        ],
    },
    {
        label: 'Logzentrale',
        items: [
            { href: '/fc/audit', label: 'FC-Logs', icon: FileSearchIcon, capability: 'audit.list' },
            { href: '/system/console-log', label: 'Konsolenlog', icon: ListIcon, permission: 'txadmin.log.view' },
            { href: '/system/action-log', label: 'Aktionslog', icon: ShieldCheckIcon, permission: 'txadmin.log.view' },
        ],
    },
    {
        label: 'System',
        items: [
            { href: '/allowlist', label: 'Allowlist', icon: ClipboardCheckIcon, permission: 'players.whitelist' },
            { href: '/admins', label: 'txAdmin-Konten', icon: UserSquare2Icon, permission: 'manage.admins' },
            { href: '/settings', label: 'Einstellungen', icon: SettingsIcon, permission: 'settings.view' },
            { href: '/system/diagnostics', label: 'Diagnosen', icon: ChartNoAxesCombinedIcon },
            { href: '/system/master-actions', label: 'Master Actions', icon: ZapIcon, master: true },
            { href: '/advanced', label: 'Erweitert', icon: SlidersHorizontalIcon, permission: 'all_permissions', advanced: true },
        ],
    },
];

function NavigationLink({ item }: { item: NavigationItem }) {
    const [exactMatch] = useRoute(item.href);
    const [location] = useLocation();
    const active = exactMatch || item.href !== '/' && location.startsWith(`${item.href}/`);
    const Icon = item.icon;
    return (
        <MainPageLink
            href={item.href}
            isActive={active}
            className={cn('fc-nav-link', active && 'active')}
        >
            <Icon />
            <span>{item.label}</span>
        </MainPageLink>
    );
}

export default function FcNavigation({ drawer = false }: { drawer?: boolean }) {
    const { hasPerm } = useAdminPerms();
    const { authorized, capabilities: allowedCapabilities } = useFcAdminAccess();
    const visible = (item: NavigationItem) => {
        if (item.advanced && !window.txConsts.showAdvanced) return false;
        if (item.master && !hasPerm('master')) return false;
        if (item.permission && !hasPerm(item.permission)) return false;
        if (!item.capability) return true;
        if (!authorized) return false;
        const capabilities = Array.isArray(item.capability) ? item.capability : [item.capability];
        return capabilities.some((capability) => allowedCapabilities.has(capability));
    };

    return (
        <aside className={cn('fc-navigation', drawer && 'drawer')}>
            <div className="fc-navigation-scroll">
                {sections.map((section) => {
                    const items = section.items.filter(visible);
                    if (!items.length) return null;
                    return (
                        <section className="fc-nav-section" key={section.label}>
                            <h2>{section.label}</h2>
                            <div>
                                {items.map((item) => <NavigationLink item={item} key={item.href} />)}
                            </div>
                        </section>
                    );
                })}
            </div>
            <div className="fc-navigation-operations">
                <ServerControls />
                <ServerSchedule />
                <div className="fc-version">
                    FC Admin · Core v{window.txConsts.txaVersion}<br />FXServer b{window.txConsts.fxsVersion}
                </div>
            </div>
        </aside>
    );
}
